export const instagramUrl = 'https://www.instagram.com/bright_digital_hubonline/'
export const checkoutUrl = 'https://complete-sports-massage-bundle.impultienda.ar/checkout'
export const offerAnchor = '#offer'
export const contactEmail = 'compradigitalyaa@gmail.com'

export const mockupUrl =
  'https://cdn.magicpatterns.com/uploads/8hwWPBsVSfUGSUNpTuGzds/Set_Profesional_de_Masaje_Deportivo.png'

export const bodyAreas = ['Session Planning', 'Lower Body', 'Upper Body', 'Event Massage', 'Sport-Specific Sessions']

export const organizedNeeds = [
  { question: 'Need a lower-body massage protocol?', answer: "It's organized." },
  { question: 'Need upper-back, shoulder, or forearm techniques?', answer: "They're organized." },
  { question: 'Need a pre-event, post-event, or maintenance massage session?', answer: "It's included." },
  {
    question: 'Need a complete 30-, 45-, or 60-minute session for a specific sport?',
    answer: "You'll find ready-to-adapt examples.",
  },
]

export const systemFlow = ['Open the system', 'Select the protocol', 'Follow the sequence', 'Adapt to your client']

export const showcaseFeatures = [
  'Professional session-planning tools',
  'Lower-body sports massage techniques',
  'Upper-body sports massage techniques',
  'Step-by-step regional protocols',
  'Client positioning and contact guidance',
  'Pre-event and post-event massage sequences',
  'Maintenance massage protocols',
  'Complete sessions organized by sport',
  '30-, 45-, and 60-minute session examples',
  'Practical modifications and precautions',
  'Professional client and business organization tools',
]

export type Volume = {
  number: string
  name: string
  intro: string
  itemsLabel?: string
  items?: string[]
  details: string[]
  tagline: string
}

export const volumes: Volume[] = [
  {
    number: '01',
    name: 'Session Planning & Protocol Selection',
    intro: 'Organize your massage appointments using clear professional decision tools.',
    details: [
      "Identify the client's main objective, select priority body regions, consider treatment preferences, and organize the available session time.",
      'Includes practical selection matrices, treatment planning workflows, realistic professional scenarios, and references to the appropriate protocols.',
      'Know where to start and how to build a more organized session.',
    ],
    tagline: 'Assess. Prioritize. Select. Plan.',
  },
  {
    number: '02',
    name: 'Lower Body Sports Massage Protocols',
    intro: 'Access step-by-step regional massage protocols for the lower body.',
    itemsLabel: 'Includes practical work for:',
    items: [
      'Gluteal region',
      'Hip',
      'Quadriceps',
      'Hamstrings',
      'Medial thigh',
      'Gastrocnemius and calf',
      'Soleus region',
      'Anterior lower leg',
      'Lateral lower leg',
      'Dorsal foot',
      'Plantar foot',
    ],
    details: [
      'Each protocol provides professional positioning guidance, contact instructions, technique sequences, estimated timing, relevant precautions, and practical adaptations.',
    ],
    tagline: 'Position. Apply. Adjust.',
  },
  {
    number: '03',
    name: 'Upper Body Sports Massage Protocols',
    intro: 'Find organized professional techniques for the upper body and trunk.',
    itemsLabel: 'Includes practical protocols for:',
    items: [
      'Posterior neck and upper trapezius',
      'Thoracic back',
      'Lumbar region',
      'Interscapular region',
      'Posterior shoulder',
      'Lateral shoulder',
      'Anterior shoulder',
      'Biceps and upper arm',
      'Triceps',
      'Forearm flexors and extensors',
      'Wrist and hand',
    ],
    details: [
      'Follow region-specific sequences with contact references, professional positioning, timing guidance, and adaptable techniques.',
    ],
    tagline: 'Locate. Apply. Adapt.',
  },
  {
    number: '04',
    name: 'Pre-Event, Post-Event & Maintenance Massage',
    intro: "Organize sports massage around the athlete's activity schedule.",
    itemsLabel: 'Access practical protocols for:',
    items: [
      'Pre-event lower-body massage',
      'Pre-event upper-body massage',
      'Short seated pre-event sessions',
      'Immediate post-event comfort sessions',
      'Post-event lower-body massage',
      'Post-event upper-body massage',
      'Focused maintenance sessions',
      'Multi-region maintenance sessions',
      'Modified maintenance sessions',
    ],
    details: [
      'Includes session timing, professional treatment sequences, practical adjustments, and event-specific considerations.',
    ],
    tagline: 'Before. After. Between Training Sessions.',
  },
  {
    number: '05',
    name: 'Complete Sports Massage Sessions by Sport',
    intro: 'Access complete, ready-to-adapt massage session examples for different athletic disciplines.',
    itemsLabel: 'Includes sessions for:',
    items: [
      'Running',
      'Soccer',
      'Cycling',
      'Swimming',
      'Tennis',
      'Strength training',
      'Basketball',
      'Volleyball',
      'Combat sports',
      'Rowing',
    ],
    details: [
      'Plus additional practical examples for hybrid athletes, trail runners, racket sports, golf, triathlon, and athletes requiring positioning modifications.',
      'Sessions are organized into 30-, 45-, and 60-minute formats, with selected body regions, treatment order, position changes, and regional protocol references.',
      "The examples are designed to be adapted to each athlete's individual needs and professional assessment.",
    ],
    tagline: 'Choose the Sport. Follow the Plan. Adapt the Session.',
  },
]

export const sampleMeta = [
  ['Sport', 'Running'],
  ['Main regions', 'Hamstrings + calves'],
  ['Total duration', '30 minutes'],
  ['Client position', 'Prone, with appropriate support'],
  ['Technique references', 'Volume 02'],
  ['Primary objective', 'Individualized comfort-focused muscle work'],
] as const

export const sampleTimeline = [
  ['Confirm the session goal and relevant precautions', '3 min'],
  ['Position the client and prepare contact', '2 min'],
  ['Posterior thigh techniques', '11 min'],
  ['Transition and client response check', '2 min'],
  ['Calf techniques', '10 min'],
  ['Final response check and completion', '2 min'],
] as const

export const protocolStructure = [
  'Client positioning',
  'Target body region',
  'Technique sequence',
  'Hand contact and movement direction',
  'Estimated application time',
  'Pressure considerations',
  'Position transitions',
  'Practical adaptations',
  'Relevant precautions',
  'Reasons to modify or stop treatment',
]

export const differentiators = [
  {
    icon: 'book',
    title: 'Ready-to-Use Professional Protocols',
    text: 'Open the corresponding volume and locate techniques already organized by body region and practical application.',
  },
  {
    icon: 'clock',
    title: 'Treatment Timing Included',
    text: 'Access example durations, treatment sequences, and position transitions to help organize appointments.',
  },
  {
    icon: 'activity',
    title: 'Different Sports & Athletic Needs',
    text: 'Consult complete session examples for runners, soccer players, cyclists, swimmers, strength athletes, and more.',
  },
  {
    icon: 'calendar',
    title: 'Event-Based Massage Options',
    text: 'Find appropriate planning and sequence references for pre-event, post-event, and maintenance massage.',
  },
  {
    icon: 'clipboard',
    title: 'Practical Professional Tools',
    text: 'Use client forms, therapist working-position references, aftercare handouts, event checklists, and communication templates.',
  },
  {
    icon: 'layers',
    title: 'Less Preparation Time',
    text: 'Keep your regional protocols, session examples, and everyday professional resources together in one organized system.',
  },
] as const
